import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { programService } from '../services/program';
import { STALE_TIME, queryClient } from '../queryClient';
import { queryKeys } from './keys';

export { programsFetchKey } from './keys';

export function usePrograms(
  academyId?: string,
  options?: { includeInactive?: boolean; enabled?: boolean }
) {
  const includeInactive = options?.includeInactive ?? false;
  const enabled = (options?.enabled ?? true) && !!academyId;
  const queryKey = queryKeys.programs.list(academyId ?? '', includeInactive);

  const { data: programs = [], isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      const list = await programService.getPrograms(academyId!, includeInactive);
      return list;
    },
    enabled,
    staleTime: STALE_TIME.MEDIUM,
  });

  const getProgramsByAcademy = useCallback(
    (id: string, includeAll = false) =>
      programs.filter((p) => p.academyId === id && (includeAll || p.isActive)),
    [programs]
  );

  const refresh = useCallback(() => refetch(), [refetch]);

  return {
    programs: academyId ? getProgramsByAcademy(academyId, includeInactive) : programs,
    isLoading,
    error: error instanceof Error ? error.message : error ? String(error) : null,
    refresh,
    getProgramsByAcademy,
  };
}

export async function fetchProgramsQuery(
  academyId: string,
  includeInactive = false,
  options?: { force?: boolean }
): Promise<void> {
  const key = queryKeys.programs.list(academyId, includeInactive);
  if (options?.force) {
    await queryClient.invalidateQueries({ queryKey: key });
  }
  await queryClient.fetchQuery({
    queryKey: key,
    queryFn: () => programService.getPrograms(academyId, includeInactive),
    staleTime: STALE_TIME.MEDIUM,
  });
}

export function invalidateProgramsQueries(academyId?: string): void {
  void queryClient.invalidateQueries({
    queryKey: queryKeys.programs.lists(),
    ...(academyId ? { predicate: (q) => q.queryKey.includes(academyId) } : {}),
  });
}
